/* Courted · the weather (T3): Google's daily forecast, read for going out. "Saturday looks good for a hike"
   when a day is fine for the time of year and you're both free for three hours of daylight; "Rain's now
   forecast for Saturday's hike" when a planned one turns. Pure, no imports: the edge function courted-nudge
   keeps a byte-identical copy (supabase/functions/courted-nudge/weather.js; tests/courted.test.js checks).

   A forecast is what courted-places' `weather` op returns: { tz, days: [{ date, type, text, icon, hi, lo,
   rain, ptype, storm, wind, sunrise, sunset }] } — °C, km/h, rain and storm as percents, the sun as ISO times.
   Google's terms let a daily forecast be kept for 24 hours; we keep it in memory for three. */

const FINE = new Set(['CLEAR', 'MOSTLY_CLEAR', 'PARTLY_CLOUDY']);
const SNOW = /SNOW|BLIZZARD/;
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dateOf = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, m - 1, d, 12); };
const isoOf = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
export const addDays = (iso, n) => { const d = dateOf(iso); d.setDate(d.getDate() + n); return isoOf(d); };
const between = (a, b) => Math.round((dateOf(b) - dateOf(a)) / 86400000);

/* An ISO time as minutes after midnight where you live (the forecast's time zone), and that day. */
export function localMinutes(isoTime, tz) {
  const t = new Date(isoTime);
  if (isNaN(t)) return null;
  try {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(t)) p[x.type] = x.value;
    return { date: p.year + '-' + p.month + '-' + p.day, minutes: (Number(p.hour) % 24) * 60 + Number(p.minute) };
  } catch (e) { return { date: isoOf(t), minutes: t.getHours() * 60 + t.getMinutes() }; }
}

/* The forecast by date, with sunrise and sunset in local minutes. */
export function byDate(fc) {
  const out = {};
  for (const d of (fc && fc.days) || []) {
    const up = d.sunrise ? localMinutes(d.sunrise, fc.tz) : null, down = d.sunset ? localMinutes(d.sunset, fc.tz) : null;
    out[d.date] = { ...d, up: up ? up.minutes : null, down: down ? down.minutes : null };
  }
  return out;
}

/* The lowest top temperature that still makes a good day out, by month (northern hemisphere; flipped south of
   the equator): a bright -5° in January is a fine winter walk, 12° in July isn't. Above 30° nobody's hiking. */
const FLOOR = [-5, -5, 5, 8, 12, 12, 12, 12, 12, 8, 5, -5];
export function floorFor(iso, lat) {
  const m = dateOf(iso).getMonth();
  return FLOOR[lat != null && lat < 0 ? (m + 6) % 12 : m];
}

/* Fine for the time of year: sunny to partly cloudy, little chance of rain or storms, not too windy, and the
   top temperature between the month's floor and 30°. */
export function goodDay(d, lat) {
  if (!d) return false;
  return FINE.has(d.type) && (d.rain ?? 100) <= 20 && (d.storm ?? 0) <= 10 && (d.wind ?? 0) <= 35
    && d.hi != null && d.hi >= floorFor(d.date, lat) && d.hi <= 30;
}
/* Wet enough to change plans: rain or snow likely, or storms possible. → 'rain' | 'snow' | 'storm' | null */
export function wetDay(d) {
  if (!d) return null;
  if ((d.storm ?? 0) >= 40) return 'storm';
  if ((d.rain ?? 0) >= 60) return SNOW.test(String(d.ptype || '') + ' ' + String(d.type || '')) ? 'snow' : 'rain';
  return null;
}

/* 14° (Canada, the UK) or 57° (the US). */
export const temp = (c, country) => (c == null ? '' : Math.round(String(country || '').toUpperCase() === 'US' ? c * 9 / 5 + 32 : c) + '°');
/* "14° and sunny" from the daytime forecast. */
export function weatherWords(d, country) {
  if (!d) return '';
  const what = String(d.text || '').toLowerCase();
  return temp(d.hi, country) + (what ? ' and ' + what : '');
}

/* The longest stretch of daylight you're both free for, from the day's free segments (minutes): no earlier
   than 8am or sunrise, ending half an hour before sunset. → { from, to } of three hours or more, or null. */
export function daylightWindow(segs, up, down, least = 180) {
  const lo = Math.max(8 * 60, up ?? 7 * 60), hi = (down ?? 17 * 60) - 30;
  let best = null;
  for (const [a, b] of segs || []) {
    const s = Math.max(a, lo), e = Math.min(b, hi);
    if (e - s >= least && (!best || e - s > best.to - best.from)) best = { from: s, to: e, whole: s === lo && e === hi };
  }
  return best;
}

/* "Saturday looks good for a hike": the first day in the next five (today too while there's daylight left)
   that's fine for the time of year, with three hours of daylight you're both free and no outdoor date on it
   already. `segs`: { date: [[a, b]] } free together with nothing planned (Home's week, or the server's copy of
   it). `busyDates`: dates with an outdoor date planned. → { date, from, to, whole, day } or null. */
export function outdoorChance({ forecast, segs, today, lat, busyDates = [] }) {
  const fc = byDate(forecast);
  for (let i = 0; i <= 5; i++) {
    const date = addDays(today, i);
    const d = fc[date];
    if (!d || busyDates.includes(date) || !goodDay(d, lat)) continue;
    const w = daylightWindow((segs || {})[date], d.up, d.down);
    if (w) return { date, from: w.from, to: w.to, whole: w.whole, day: d };
  }
  return null;
}

/* "Today", "Tomorrow", "Saturday". */
export function dayName(date, today) {
  const n = between(today, date);
  return n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : DAYS[dateOf(date).getDay()];
}
const ampm = (m) => { const h = Math.floor(m / 60) % 24, mm = m % 60; return (h % 12 || 12) + (mm ? ':' + String(mm).padStart(2, '0') : '') + (h >= 12 ? 'pm' : 'am'); };
/* The nudge's words. `hike`: 'hike' or 'walk' (the UK). `ideas`: how many outdoor places you both like.
   → { title: 'Saturday looks good for a hike', line: '17° and partly sunny, and you're both free from 10am.' } */
export function chanceWords(ch, today, { country, hike = 'hike', partner = true } = {}) {
  if (!ch) return null;
  const free = (partner ? 'you’re both free ' : 'you’re free ') + (ch.whole ? 'all day' : ch.to >= (ch.day.down ?? 17 * 60) - 30 ? 'from ' + ampm(ch.from) : 'from ' + ampm(ch.from) + ' to ' + ampm(ch.to));
  return { title: dayName(ch.date, today) + ' looks good for a ' + hike, line: weatherWords(ch.day, country).replace(/^./, (c) => c.toUpperCase()) + ', and ' + free + '.' };
}
/* "Rain's now forecast for Saturday's hike" (a planned outdoor date that's turned wet). */
export function wetWords(kind, date, today, hike = 'hike') {
  if (!kind) return null;
  const n = dayName(date, today);
  const what = kind === 'snow' ? 'Snow’s' : kind === 'storm' ? 'A storm’s' : 'Rain’s';
  return what + ' now forecast for ' + (n === 'Today' ? 'today’s' : n === 'Tomorrow' ? 'tomorrow’s' : n + '’s') + ' ' + hike;
}
